import React from 'react';
import { compose, lifecycle, withHandlers, withPropsOnChange, withState } from 'recompose';
import { reduxForm, Field, FormSection } from 'redux-form';
import { connect } from 'react-redux';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
import { TextField, Select } from 'redux-form-material-ui';
import Grid from '@material-ui/core/Grid';
import { get, find, uniq, isArray } from 'lodash';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import { Link } from 'react-router-dom';
import { users } from '../_api';
import withLoader from '../../app/withLoader';
import PersonIcon from '@material-ui/icons/Person';
import { Autocomplete } from '../../../components/forms/Autocomplete';

const required = (value) => (value || typeof value === 'number' ? undefined : 'Vyžadováno');
const email = (value) =>
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)
    ? 'Neplatný formát emailu'
    : undefined;
const number = (value) => (value && isNaN(Number(value)) ? 'Musí být číslo' : undefined);
const maxLength = (max) => (value) =>
  value && value.length > max ? `Je povoleno maximálně ${max} znaků` : undefined;
const maxLengt16 = maxLength(16);
const maxLength32 = maxLength(32);
const maxLength64 = maxLength(64);
const maxLength256 = maxLength(256);

const styles = (theme) => ({
  section: {
    marginBottom: 25,
  },
  textFields: {
    width: '80%',
  },
  buttons: {
    margin: '0 5px',
  },
});

const enhance = compose(
  withStyles(styles),
  withState('emails', 'setEmails', []),
  withState('showLink', 'setShowLink', 0),
  withLoader,
  connect(
    (state) => ({
      userAttributes: get(state, 'app.userAttributes', []),
    }),
    null
  ),
  withHandlers({
    duplicateEmail:
      ({ emails, setShowLink }) =>
      (value) => {
        const isExistPerson = find(emails, { email: value });
        setShowLink(get(isExistPerson, 'id', 0));
        return isExistPerson ? 'Osoba již existuje' : undefined;
      },
  }),
  withHandlers({
    loadUsers:
      ({ toggleLoader, setEmails, initialValues }) =>
      async () => {
        toggleLoader();
        const resp = await users(false);
        if (resp) {
          const email = {
            email: get(initialValues, 'userInfo.email'),
            id: get(initialValues, 'userInfo.personId'),
          };
          const emails = [];
          get(resp, 'items', []).forEach((item) => {
            const mail = get(item, 'email', null);
            const id = get(item, 'id', null);
            if (mail) {
              emails.push({ email: mail, id });
            }
          });
          // Seznam existujících emailů.
          // Pozn: V případě aktualizace uživatele odebrán email aktuálního uživatele
          // const emailsItem = without(uniq(emails), email);
          const emailsItem = [];
          uniq(
            emails.forEach((em) => {
              if (em.email !== email.email) {
                emailsItem.push(em);
              }
            })
          );
          setEmails(emailsItem);
        }
        toggleLoader(false);
      },
  }),
  lifecycle({
    async componentDidMount() {
      const { loadUsers } = this.props;
      await loadUsers();
    },
  }),
  withPropsOnChange(['userAttributes'], ({ userAttributes }) => {
    const userAttributesWithoutHeight = [];
    let userHeight = null;
    if (isArray(userAttributes)) {
      userAttributes.forEach((userAttribute) => {
        if (get(userAttribute, 'name') === 'Výška') {
          userHeight = { ...userAttribute };
        } else {
          userAttributesWithoutHeight.push(userAttribute);
        }
      });
    }

    return { userHeight, userAttributesWithoutHeight };
  }),
  reduxForm({ form: 'userForm' })
);

export default enhance(
  ({
    classes,
    handleSubmit,
    setEdit,
    userHeight,
    userAttributesWithoutHeight,
    action,
    duplicateEmail,
    showLink,
  }) => (
    <form className={classes.form} onSubmit={handleSubmit}>
      <FormSection name="userInfo">
        <div className={classes.section}>
          <Grid container spacing={8}>
            <Grid item xs={3}>
              <Field
                name="surname"
                component={TextField}
                label="Příjmení"
                className={classes.textFields}
                validate={[required, maxLength64]}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="forename"
                component={TextField}
                label="Jméno"
                className={classes.textFields}
                validate={[required, maxLength64]}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="email"
                component={TextField}
                label="Email"
                className={classes.textFields}
                validate={[email, duplicateEmail, maxLength256]}
              />
              {showLink > 0 && (
                <Tooltip title="Přepnout na existující osobu">
                  <IconButton
                    aria-label="openLink"
                    component={(props) => <Link to={`/users/${showLink}`} {...props} />}
                    style={{ margin: '4px' }}
                  >
                    <PersonIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
            <Grid item xs={3}>
              <Field
                name="phone"
                component={TextField}
                label="Telefon"
                className={classes.textFields}
                validate={[required, maxLengt16]}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="birthDate"
                component={TextField}
                label="Datum narození"
                type="date"
                className={classes.textFields}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </div>
      </FormSection>
      <div className={classes.section}>
        <Grid container spacing={8}>
          {userAttributesWithoutHeight.map((item) => (
            <Grid key={item.id} item xs={3}>
              <FormControl className={classes.textFields}>
                <InputLabel htmlFor={item.name}>{item.name}</InputLabel>
                <Field name={`attributes.${item.id}`} component={Select} placeholder={item.name}>
                  {item.values.map((item2) => (
                    <MenuItem key={item2.id} value={item2.id}>
                      {item2.value}
                    </MenuItem>
                  ))}
                </Field>
              </FormControl>
            </Grid>
          ))}
          {userHeight && (
            <Grid item xs={3}>
              <React.Fragment>
                <Autocomplete
                  name={`attributes.${userHeight.id}`}
                  placeholder="Vyberte"
                  options={userHeight.values.map((item2) => ({
                    label: item2.value,
                    value: item2.id,
                  }))}
                  label={userHeight.name}
                  style={{ width: '80%' }}
                />
              </React.Fragment>
            </Grid>
          )}
        </Grid>
      </div>
      <div className={classes.section}>
        <FormSection name="address">
          <Grid container spacing={8}>
            <Grid item xs={3}>
              <Field
                name="street"
                component={TextField}
                label="Ulice"
                className={classes.textFields}
                validate={[required, maxLength64]}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="streetNumber"
                component={TextField}
                label="Číslo popisné"
                className={classes.textFields}
                validate={[required, maxLengt16]}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="city"
                component={TextField}
                label="Město"
                className={classes.textFields}
                validate={[required, maxLength64]}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="postalCode"
                component={TextField}
                label="Směrovací číslo"
                className={classes.textFields}
                validate={[required, number]}
              />
            </Grid>
          </Grid>
        </FormSection>
      </div>
      <FormSection name="identity">
        <Grid container spacing={8}>
          <Grid item xs={3}>
            <Field
              name="userIdNumber"
              component={TextField}
              label="Rodné číslo"
              className={classes.textFields}
              validate={[maxLength32]}
            />
          </Grid>
          <Grid item xs={3}>
            <FormControl className={classes.textFields}>
              <InputLabel htmlFor="typeId">Typ průkazu</InputLabel>
              <Field name="typeId" component={Select} placeholder="typeId" validate={[required]}>
                <MenuItem value="ID_CARD">Občanský průkaz</MenuItem>
                <MenuItem value="PASSPORT">Pas</MenuItem>
                <MenuItem value="DRIVER_LICENSE">Řidičský průkaz</MenuItem>
              </Field>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <Field
              name="serialNumber"
              component={TextField}
              label="Číslo průkazu"
              className={classes.textFields}
              validate={[required, maxLength32]}
            />
          </Grid>
          <Grid item xs={3}>
            <Field
              name="expiration"
              component={TextField}
              label="Platnost průkazu do"
              className={classes.textFields}
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              validate={[required]}
            />
          </Grid>
        </Grid>
      </FormSection>
      <Grid container spacing={8} />
      <div style={{ marginTop: 20 }}>
        <Button type="submit" variant="contained" color="primary" className={classes.buttons}>
          Uložit
        </Button>
        {action === 'add' ? (
          <Button
            variant="contained"
            color="secondary"
            component={(props) => <Link to="/users/" {...props} />}
            className={classes.buttons}
          >
            Zrušit
          </Button>
        ) : (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setEdit(false)}
            className={classes.buttons}
          >
            Zrušit
          </Button>
        )}
      </div>
    </form>
  )
);



// WEBPACK FOOTER //
// ./src/modules/users/detail/userForm.js